import React, { useEffect } from 'react';
import { arrayOf, bool, string } from 'prop-types';
import { compose } from 'redux';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { isMainSearchTypeKeywords } from '../../util/search';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import { Page, LayoutSingleColumn, NamedLink, IconSpinner } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import {
  fetchAllStates,
  fetchSeoCategories,
  fetchSeoCategoriesInProgressSelector,
  fetchSeoPagesSelector,
  getStatesData,
  getBlogsSelector,
} from '../../ducks/strapi.duck';
import { formatKebabToTitle, isArrayLength } from '../../util/genericHelpers';
import { stateFromSlug } from '../../util/interlinking';
import { getStateContent } from '../../util/stateSeoContent';
import StateRelatedLinks from './StateRelatedLinks';

import css from './StateListsPage.module.css';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Sort city categories alphabetically by the city name portion of their title.
 * Titles are like "Trailer Rentals in Austin, Texas" — we sort by "Austin".
 */
const processSeoCategories = seoCategories => {
  if (!Array.isArray(seoCategories)) return [];

  return [...seoCategories]
    .map(category => {
      const title = String(category?.title || '');
      const parts = title.split(' in ');
      return { name: parts[1]?.trim() || title, original: category };
    })
    .filter(item => item.name)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(item => item.original);
};

/**
 * Derive a human-readable state name from the slug.
 * "trailer-rentals-texas" → "Texas"
 */
const stateNameFromSlug = slug => {
  if (!slug) return '';
  return String(slug)
    .replace(/^trailer-rentals-/, '')
    .split('-')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

// ─── Quick Answer Box ─────────────────────────────────────────────────────────

const QuickAnswerBox = ({ text }) => {
  if (!text) return null;
  return (
    <div className={css.quickAnswerBox}>
      <div className={css.quickAnswerLabel}>Quick Answer</div>
      <p className={css.quickAnswerText}>{text}</p>
    </div>
  );
};

// ─── State Intro Editorial ────────────────────────────────────────────────────

const StateIntro = ({ sections }) => {
  if (!isArrayLength(sections)) return null;
  return (
    <div className={css.stateIntro}>
      {sections.map((section, idx) => (
        <div key={idx} className={css.introSection}>
          <h2 className={css.introHeading}>{section.heading}</h2>
          {section.body.split('\n\n').map((para, pIdx) => {
            if (!para.trim()) return null;
            // Convert **bold** markers to <strong> inline
            const parts = para.split(/\*\*([^*]+)\*\*/g);
            const rendered = parts.map((part, i) =>
              i % 2 === 1 ? <strong key={i}>{part}</strong> : part
            );
            // Detect bullet list lines (lines starting with "- ")
            const lines = para.trim().split('\n');
            const isBulletList = lines.every(l => l.trim().startsWith('- '));
            if (isBulletList) {
              return (
                <ul key={pIdx} className={css.introBullets}>
                  {lines.map((line, lIdx) => {
                    const text = line.replace(/^-\s*/, '').trim();
                    const lParts = text.split(/\*\*([^*]+)\*\*/g);
                    return (
                      <li key={lIdx}>
                        {lParts.map((p, pi) =>
                          pi % 2 === 1 ? <strong key={pi}>{p}</strong> : p
                        )}
                      </li>
                    );
                  })}
                </ul>
              );
            }
            return <p key={pIdx} className={css.introPara}>{rendered}</p>;
          })}
        </div>
      ))}
    </div>
  );
};

// ─── City Card Grid ───────────────────────────────────────────────────────────

const CityGrid = ({ categories, stateSlug, searchInProgress }) => {
  const sorted = processSeoCategories(categories);
  return (
    <div className={css.cityGridSection}>
      <h2 className={css.cityGridHeading}>Browse Cities in This State</h2>
      {searchInProgress ? (
        <div className={css.iconSpinner}><IconSpinner /></div>
      ) : null}
      {isArrayLength(sorted) ? (
        <ul className={css.cityGrid}>
          {sorted.map(s => (
            <li key={s?.slug} className={css.cityCard}>
              <NamedLink
                name="SeoCategoryPage"
                params={{ title: s?.slug, state: stateSlug }}
                className={css.cityCardLink}
              >
                {s?.title}
              </NamedLink>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

// ─── FAQ Section ─────────────────────────────────────────────────────────────

const FaqSection = ({ faqs }) => {
  if (!isArrayLength(faqs)) return null;
  return (
    <div className={css.faqSection}>
      <h2 className={css.faqHeading}>Frequently Asked Questions</h2>
      <dl className={css.faqList}>
        {faqs.map((faq, idx) => (
          <div key={idx} className={css.faqItem}>
            <dt className={css.faqQuestion}>{faq.question}</dt>
            <dd className={css.faqAnswer}>{faq.answer}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

// ─── Breadcrumb ───────────────────────────────────────────────────────────────

const StateBreadcrumb = ({ stateName }) => (
  <nav className={css.breadcrumb} aria-label="Breadcrumb">
    <NamedLink name="LandingPage" className={css.crumb}>Home</NamedLink>
    <span className={css.crumbSep}>/</span>
    <NamedLink name="SeoCategoriesPage" className={css.crumb}>Trailer Rentals by State</NamedLink>
    <span className={css.crumbSep}>/</span>
    <span className={css.crumbCurrent}>{stateName}</span>
  </nav>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const StateListsPageComponent = ({ scrollingDisabled, intl }) => {
  const dispatch = useDispatch();
  const params = useParams();
  const currentState = params?.state; // e.g. "trailer-rentals-texas"

  useEffect(() => {
    dispatch(fetchSeoCategories({ state: currentState }));
  }, [currentState]);

  const seoCategories = useSelector(fetchSeoPagesSelector);
  const searchInProgress = useSelector(fetchSeoCategoriesInProgressSelector);
  const { statesData } = useSelector(getStatesData) || {};
  const allBlogs = useSelector(getBlogsSelector) || [];

  // ── Resolve state name + Strapi seo object ──────────────────────────────
  const stateObj = stateFromSlug(currentState, statesData || []);
  const stateName =
    (stateObj && stateObj.title) || stateNameFromSlug(currentState);

  // Strapi seo fields (metaTitle, metaDescription) from the states list
  const strapiSeo = stateObj?.seo || null;

  const marketplaceName = 'BigLoads';

  // ── Page meta ──────────────────────────────────────────────────────────
  const pageTitle =
    (strapiSeo?.metaTitle && strapiSeo.metaTitle.trim()) ||
    `Trailer Rentals in ${stateName} | ${marketplaceName}`;
  const pageDescription =
    (strapiSeo?.metaDescription && strapiSeo.metaDescription.trim()) ||
    `Find local trailer rentals in ${stateName} on ${marketplaceName}. Browse utility trailers, flatbeds, enclosed cargo trailers and more — rent directly from local owners at competitive prices.`;

  // ── In-code editorial content ──────────────────────────────────────────
  const stateContent = getStateContent(currentState);
  const quickAnswer = stateContent?.quickAnswer || null;
  const introSections = stateContent?.intro || null;
  const faqs = stateContent?.faqs || null;

  // ── Structured data ─────────────────────────────────────────────────────
  const siteUrl = process.env.REACT_APP_MARKETPLACE_ROOT_URL || 'https://bigloads.com';
  const pageUrl = `${siteUrl}/states/${currentState}`;

  const breadcrumbSchema = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Trailer Rentals by State',
        item: `${siteUrl}/states`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: `Trailer Rentals in ${stateName}`,
        item: pageUrl,
      },
    ],
  };

  const faqSchema = isArrayLength(faqs)
    ? {
        '@type': 'FAQPage',
        mainEntity: faqs.map(faq => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      }
    : null;

  const schemas = [breadcrumbSchema, faqSchema].filter(Boolean);

  if (!seoCategories) {
    return (
      <LayoutSingleColumn
        className={css.root}
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        <div className={css.iconSpinner}>
          <IconSpinner />
        </div>
      </LayoutSingleColumn>
    );
  }

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={pageTitle}
      description={pageDescription}
      schema={schemas}
    >
      <LayoutSingleColumn
        className={css.root}
        topbar={<TopbarContainer isWhiteBgNav={false} isHeaderSticky={true} />}
        footer={<FooterContainer />}
      >
        <div className={css.pageRoot}>

          <div className={css.contentColumn}>

            {/* Breadcrumb */}
            <StateBreadcrumb stateName={stateName} />

            {/* 1. Title + intro subtitle + quick answer */}
            <div className={css.pageHeader}>
              <span className={css.eyebrow}>BigLoads — Peer-to-Peer Trailer Rental</span>
              <h1 className={css.h1}>
                Trailer Rentals in {stateName} — Browse by City
              </h1>
              <p className={css.headerSubtitle}>
                {pageDescription}
              </p>
            </div>

            {/* Quick Answer featured-snippet box (tucked under the title) */}
            <QuickAnswerBox text={quickAnswer} />

            {/* 2. Browse Cities in this state */}
            <CityGrid
              categories={seoCategories}
              stateSlug={currentState}
              searchInProgress={searchInProgress}
            />

            {/* 3. Content about that state */}
            <StateIntro sections={introSections} />

            {/* 4. FAQ */}
            <FaqSection faqs={faqs} />

            {/* 5. Guides / rentals-by-state links */}
            <StateRelatedLinks
              stateSlug={currentState}
              states={statesData || []}
              blogs={allBlogs}
            />

          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

StateListsPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  intl: intlShape.isRequired,
};

const EnhancedStateListsPage = props => {
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const history = useHistory();
  const intl = useIntl();

  return (
    <StateListsPageComponent
      routeConfiguration={routeConfiguration}
      marketplaceName={config.marketplaceName}
      isKeywordSearch={isMainSearchTypeKeywords(config)}
      history={history}
      intl={intl}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const StateListsPage = compose(connect(mapStateToProps))(EnhancedStateListsPage);

export default StateListsPage;
